import { useEffect, useRef } from 'react';

import { BASE_URL } from '../../api/auth-fetch';
import { useI18n } from '../../i18n/provider';

/**
 * La prueba de trabajo del inicio de sesión (`14 §5.1`).
 *
 * Es el widget de ALTCHA (MIT, auto-hospedado: pide el reto a
 * `GET /auth/captcha` de **este** backend y lo resuelve en un worker del
 * navegador; ningún tercero ve la visita). Con `auto="onload"` empieza solo, y
 * en medio segundo la casilla aparece marcada: para una persona es un tic que
 * se marca a sí mismo, para un script son quinientos milisegundos de CPU por
 * cada intento —que es lo que permite que los límites de `12 §1.6` sean tan
 * anchos como son—.
 *
 * Se crea de forma imperativa y no como JSX porque es un *custom element*
 * cargado bajo demanda: React no sabe de él en tiempo de compilación y el
 * paquete no se importa hasta que la pantalla de login se pinta, así que ni
 * pesa en el resto de la app ni existe en el jsdom de los tests (que lo
 * sustituyen entero).
 *
 * `onChange` recibe el *payload* cuando el reto está resuelto y `null` en
 * cualquier otro estado. Cada reto vale para **un** intento: el backend lo
 * gasta al aceptarlo, acierte o no la contraseña, así que la pantalla monta un
 * widget nuevo (cambiando la `key`) después de cada respuesta.
 */
export function CaptchaWidget({ onChange, error }: { onChange: (payload: string | null) => void; error?: string | undefined }) {
  const host = useRef<HTMLDivElement>(null);
  const { locale } = useI18n();
  // La última versión del callback, para que un `onChange` nuevo en cada
  // render no destruya y vuelva a crear el widget (y con él, el reto).
  const latest = useRef(onChange);
  latest.current = onChange;

  useEffect(() => {
    let cancelled = false;
    let widget: HTMLElement | null = null;

    const onState = (event: Event) => {
      const detail = (event as CustomEvent<{ state?: string; payload?: string }>).detail;
      latest.current(detail?.state === 'verified' && detail.payload ? detail.payload : null);
    };

    void Promise.all([import('altcha'), import('altcha/i18n/es-es'), import('altcha/i18n/en')]).then(() => {
      if (cancelled || host.current === null) {
        return;
      }

      widget = document.createElement('altcha-widget');
      widget.setAttribute('challenge', `${BASE_URL}/auth/captcha`);
      widget.setAttribute('auto', 'onload');
      widget.setAttribute('type', 'checkbox');
      widget.setAttribute('language', locale);
      widget.setAttribute('name', 'captcha');
      // Sin logo ni pie: la tarjeta de acceso no anuncia qué la protege, igual
      // que no anuncia qué producto es.
      widget.setAttribute('configuration', JSON.stringify({ hideFooter: true, hideLogo: true, minDuration: 300 }));
      widget.addEventListener('statechange', onState);
      host.current.replaceChildren(widget);
    });

    return () => {
      cancelled = true;
      widget?.removeEventListener('statechange', onState);
      widget?.remove();
      latest.current(null);
    };
  }, [locale]);

  return (
    <div className="space-y-1.5">
      <div
        ref={host}
        data-testid="captcha"
        className="min-h-12"
        style={
          {
            '--altcha-border-width': '1px',
            '--altcha-border-radius': '0.75rem',
            '--altcha-border-color': error ? 'var(--color-danger)' : 'var(--color-line)',
            '--altcha-color-base': 'var(--color-paper)',
            '--altcha-color-base-content': 'var(--color-ink)',
            '--altcha-color-primary': 'var(--color-primary)',
            '--altcha-color-primary-content': 'var(--color-primary-ink)',
            '--altcha-color-success': 'var(--color-success)',
            '--altcha-color-error': 'var(--color-danger)',
            '--altcha-checkbox-border-color': 'var(--color-line-strong)',
            '--altcha-max-width': '100%',
          } as React.CSSProperties
        }
      />
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
